import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import CheckboxTree from 'react-checkbox-tree';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, MinusIcon } from 'lucide-react';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import { cn } from 'src/domains/common/components/utils';
import { Button } from 'src/domains/common/components/button/button';
import { Badge } from 'src/domains/common/components/badge/badge';
import { ClickOutsideHandler } from 'src/domains/common/components/clickOutside';
import { MultiOptionSelectState } from './multi-option-select.state';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';

export interface NodesType {
    id: string;
    value: string;
    label: React.ReactNode;
    children?: ChildType[];
}

export interface ChildType {
    id: string;
    value: string;
    label: React.ReactNode;
}

interface MultiOptionSelectPropsType {
    label?: string;
    nodes: NodesType[];
    formInputState: FormInputState<string, string>;
    errors: string[];
    disabled?: boolean;
    isClearAll?: boolean;
    dataTest?: string;
    dataTestDropdown?: string;
}

export const MultiOptionSelect = observer(
    'MultiOptionSelect',
    ({
        label,
        nodes,
        formInputState,
        errors,
        disabled,
        isClearAll = true,
        dataTest,
        dataTestDropdown,
    }: MultiOptionSelectPropsType) => {
        const [state] = useState(() => new MultiOptionSelectState(formInputState));
        const error = errors.length === 0 ? null : errors[0] ?? null;
        const isDisabled = disabled === true;

        return (
            <ClickOutsideHandler onClickOutside={() => state.setExpanded(false)}>
                <div className='flex flex-col gap-2'>
                    <div
                        className={cn(
                            'relative flex flex-col flex-auto gap-2 multi-option-select-wrapper',
                            isDisabled === false ? '' : 'cursor-not-allowed opacity-50 pointer-events-none'
                        )}
                    >
                        <label className='text-sm'>{label}</label>
                        <div
                            data-test={dataTest}
                            onClick={() => state.toggleExpanded()}
                            className={cn(
                                'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input border-solid bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 relative',
                                error === null ? '' : 'border-destructive'
                            )}
                        >
                            <span>Select</span>
                            <ChevronDownIcon className={cn('w-4 h-4 opacity-50', state.isExpanded && 'rotate-180')} />
                        </div>

                        {state.isExpanded ? (
                            <div
                                data-test={dataTestDropdown}
                                className='top-16 custom-scrollbar scrollbar-gutter-stable p-2 absolute left-0 mt-1 mb-1 z-50 flex flex-col w-full max-h-96 min-w-[8rem] rounded-md border border-solid border-border bg-popover text-popover-foreground shadow-md transition-opacity duration-300 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out gap-4'
                            >
                                {isClearAll ? (
                                    <Button
                                        variant='outline'
                                        onClick={() => state.clearChecked()}
                                        className='self-end'
                                    >
                                        Clear All
                                    </Button>
                                ) : null}

                                <CheckboxTree
                                    nodes={nodes}
                                    checked={state.checked}
                                    expanded={state.expanded}
                                    onCheck={(checked) => state.handleCheck(checked)}
                                    onExpand={(expanded) => state.handleExpand(expanded)}
                                    expandOnClick
                                    showNodeIcon={false}
                                    icons={{
                                        check: (
                                            <CheckIcon className='block rounded size-full text-primary-foreground bg-primary' />
                                        ),
                                        expandClose: <ChevronDownIcon className='size-full text-foreground' />,
                                        expandOpen: <ChevronUpIcon className='size-full text-foreground' />,
                                        halfCheck: (
                                            <MinusIcon className='block rounded size-full bg-primary text-primary-foreground' />
                                        ),
                                    }}
                                />
                            </div>
                        ) : null}

                        <div className='flex flex-wrap gap-2 py-2 empty:hidden'>
                            {state.visibleCheckedWithLabels.map(({ value, label }) => (
                                <Badge
                                    key={value}
                                    variant='secondary'
                                    icon='close'
                                    onClick={() => state.handleRemoveChecked(value)}
                                >
                                    {label}
                                </Badge>
                            ))}
                            {state.visibleChecked.remaining > 0 && state.showAllBadges === false && (
                                <Badge
                                    variant='secondary'
                                    onClick={() => state.toggleShowAllBadges()}
                                >
                                    +{state.visibleChecked.remaining}
                                </Badge>
                            )}
                        </div>
                    </div>
                    {error === null ? null : <span className='text-destructive text-sm'>{error}</span>}
                </div>
            </ClickOutsideHandler>
        );
    }
);
